import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { setActiveTab } from '@features/project-management-tools/redux/myProjectSlice';

import CommonImage from '@/components/CommonImage';
import { useGetAllBrandsQuery } from '@/api/commonApis';
import { storeItemInGlobalFilter } from '@/store/actions/FilterAction';
import { filterFieldType } from '@/features/filter/static/filterUtil';
import CustomTooltip from '@/components/CustomTooltip';
import { replaceUnderLine } from '@/utils/Util';
import { renameStatus } from '@/features/project-management-tools/utils';
import useClickOutside from '@/hooks/useClickOutside';

// generate unique Type for dropdown
const getUniqueProjetType = (brandName, projectTypeConstant, status) => {
	const safeStatus = (status || '').replace(/\s+/g, '_');
	const safeBrand = (brandName || '').replace(/\s+/g, '_');
	const safeConstant = (projectTypeConstant || '').replace(/\s+/g, '_');

	return `${safeBrand}_${safeConstant}_${safeStatus}`;
};

const ProjectBrandCard = ({ brandResponse, statusMap }) => {
	const [visibleCounts, setVisibleCounts] = useState({});
	const [selectedProjectType, setSelectedProjectType] = useState(null);
	const dispatch = useDispatch();
	const cardRef = useRef(null);

	useClickOutside(cardRef, () => setSelectedProjectType(null));

	const { data: brandsWithLogos } = useGetAllBrandsQuery({
		params: {
			page: 0,
			size: 1000,
			sort: 'statusUpdatedAt,desc'
		}
	});

	const findBrand = brandsWithLogos?.find((brand) => parseInt(brand?.id) === parseInt(brandResponse?.id));

	const getVisibleCount = (status) => {
		return visibleCounts[status] || 4;
	};

	const handleViewMore = (status) => {
		const totalItems = statusMap[status]?.length || 0;
		setVisibleCounts((prev) => ({
			...prev,
			[status]: totalItems
		}));
	};

	const handleViewLess = (status) => {
		setVisibleCounts((prev) => ({
			...prev,
			[status]: 4
		}));
	};

	const handleViwAllProjectNavigation = (projectDetails) => {
		const { projectType, brandId, projectStatus, brandName } = projectDetails;

		dispatch(setActiveTab('KANBAN'));
		dispatch(
			storeItemInGlobalFilter({
				name: 'brandIds',
				key: brandId,
				value: brandName,
				identifier: `${brandId}_${brandName}`,
				checked: true
			})
		);
		dispatch(
			storeItemInGlobalFilter({
				name: 'statusList',
				key: projectStatus,
				value: projectStatus,
				identifier: projectStatus,
				checked: true
			})
		);
		dispatch(
			storeItemInGlobalFilter({
				name: filterFieldType['PROJECT_TYPE']['idName'],
				key: projectType,
				value: replaceUnderLine(projectType),
				identifier: `${filterFieldType['PROJECT_TYPE']['idName']}_${projectType}`,
				checked: true
			})
		);
	};

	return (
		<div className="task-column project-column">
			{/* Brand Info */}

			<div className="project-column-header mb-3 d-flex align-items-center justify-content-start">
				<CustomTooltip text={brandResponse?.name}>
					{findBrand?.brandLogoDocResponse?.docUrl ? (
						<CommonImage
							src={findBrand?.brandLogoDocResponse?.docUrl}
							width={110}
							height={40}
							isStatic={false}
						/>
					) : (
						<p
							style={{
								visibility: 'hidden'
							}}
						>
							{brandResponse?.name}
						</p>
					)}
				</CustomTooltip>
			</div>

			{/* Project Type Info */}
			<div className="project-column-body">
				{Object.keys(statusMap || {})?.map((status) => {
					const currentVisibleCount = getVisibleCount(status);
					const totalItems = statusMap[status]?.length || 0;
					const totalProjects =
						statusMap[status]?.reduce(
							(acc, projectType) => acc + projectType?.projects?.length,
							0
						) || 0;
					const remainingItems = Math.max(0, totalItems - currentVisibleCount);

					return (
						<div className="single-project-box active-collapse mb-2" key={status}>
							<div className="box-header">
								<p className="gray_dark_01 regular-14 weight-600">
									{renameStatus(status).toUpperCase()} {`(${totalProjects})`}
								</p>
							</div>
							{/* Project Type list */}
							<div className="box-content mt-12">
								{statusMap[status]?.length > 0 &&
									statusMap[status]?.slice(0, currentVisibleCount)?.map((projectType) => {
										return (
											<div className="single-list pb-2 position-relative">
												{/* Project Type list  */}
												<div
													className="d-flex align-items-center justify-content-start cursor-pointer"
													onClick={() =>
														setSelectedProjectType(
															getUniqueProjetType(
																brandResponse?.name,
																projectType?.constant,
																status
															)
														)
													}
												>
													<p className="regular-14">
														{projectType?.name} {`(${projectType?.count})`}
													</p>

													<CommonImage
														className="arrow-icon"
														src="/icons/down-arrow-gray-dark-02.svg"
														alt="arrow"
														width={20}
														height={20}
													/>
												</div>
												{/* projct list Dropdown  */}
												{selectedProjectType ===
													getUniqueProjetType(
														brandResponse?.name,
														projectType?.constant,
														status
													) && (
													<div
														className="project-list-popup position-absolute shadow-8dp scroll-y-label"
														ref={cardRef}
													>
														<p
															className="view-more d-flex align-items-center cursor-pointer"
															onClick={(e) => {
																e.stopPropagation();
																handleViwAllProjectNavigation({
																	projectType: projectType?.constant,
																	brandName: brandResponse?.name,
																	brandId: brandResponse?.id,
																	projectStatus: status
																});
															}}
														>
															<span className="gray_dark_01 regular-14 weight-400 cursor-pointer">
																View all
															</span>
														</p>
														{projectType?.projects?.map((project) => (
															<p
																key={project?.id}
																onClick={(e) => {
																	e.stopPropagation();
																	window.open(
																		`/my-project/${project?.id}`,
																		'_blank'
																	);
																}}
															>
																<span className="gray_dark_01 regular-14 weight-400 cursor-pointer mr-1">
																	{project?.completionPercentage}%
																</span>
																<span className="mr-1">|</span>
																<span className="gray_dark_01 regular-14 weight-400 cursor-pointer mr-1">
																	{replaceUnderLine(project?.priority)}
																</span>
																<span className="mr-1">|</span>
																<span className="gray_dark_01 regular-14 weight-400 cursor-pointer mr-1">
																	{project?.name}
																</span>
															</p>
														))}
													</div>
												)}
											</div>
										);
									})}
							</div>
							{(totalItems > currentVisibleCount ||
								(currentVisibleCount > 4 && totalItems > 4)) && (
								<div
									className="view-more d-flex align-items-center cursor-pointer"
									onClick={() =>
										totalItems > currentVisibleCount
											? handleViewMore(status)
											: handleViewLess(status)
									}
								>
									<p className="gray_dark_01 regular-14 weight-400 pb-1 mr-1">
										{totalItems > currentVisibleCount
											? `View all (${remainingItems})`
											: 'View less'}
									</p>
									<CommonImage
										className={totalItems > currentVisibleCount ? '' : 'arrow-icon'}
										src="/icons/down-arrow-gray-dark-01.svg"
										alt="arrow"
										width={20}
										height={20}
										style={
											totalItems > currentVisibleCount
												? {}
												: { transform: 'rotate(180deg)' }
										}
									/>
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default ProjectBrandCard;
